// TimeClock.jsx - Employee time clock with daily notes, weekly editing, and end-of-week checklist
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

// Fixed end-of-week tasks for shop users (edit here to change the Friday routine)
const CHECKLIST_TASKS = [
  { key: 'runlist', title: "Go over next week's run list", desc: 'Make sure all parts are ordered for next week\'s vehicles' },
  { key: 'cleanup', title: 'Pick up the shop', desc: 'Clean and organize the work area' },
  { key: 'returns', title: 'Parts returns', desc: 'List every part that needs to go back — this list is sent to the office' },
  { key: 'purchases', title: 'Update personal purchases', desc: 'Log anything you bought on the card for yourself this week' },
  { key: 'timesheet', title: 'Verify your time sheet', desc: 'Check your hours are right — fix anything before the week closes' },
];

// Monday of the week containing d (local time)
const getWeekStartStr = (d = new Date()) => {
  const dd = new Date(d);
  const day = dd.getDay();
  dd.setDate(dd.getDate() - day + (day === 0 ? -6 : 1));
  return `${dd.getFullYear()}-${String(dd.getMonth() + 1).padStart(2, '0')}-${String(dd.getDate()).padStart(2, '0')}`;
};

const toTimeInput = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const toDateInput = (ts) => {
  const d = ts ? new Date(ts) : new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const fmtMoney = (n) => {
  const val = parseFloat(n) || 0;
  return `$${Math.abs(val).toFixed(2)}`;
};

const TimeClock = () => {
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';
  const isShopUser = currentUser?.showShopTasks || currentUser?.role === 'shop';
  
  const [clockStatus, setClockStatus] = useState({ clockedIn: false, entry: null });
  const [loading, setLoading] = useState(true);
  const [clockingIn, setClockingIn] = useState(false);
  const [clockingOut, setClockingOut] = useState(false);
  const [showClockOutModal, setShowClockOutModal] = useState(false);
  const [dailyNotes, setDailyNotes] = useState('');
  const [timeEntries, setTimeEntries] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [filterUserId, setFilterUserId] = useState('');
  const [users, setUsers] = useState([]);

  // Weekly summary + checklist
  const [weekSummary, setWeekSummary] = useState(null);
  const [checklistItems, setChecklistItems] = useState({});
  const [returnsList, setReturnsList] = useState(['']);
  const [noReturns, setNoReturns] = useState(false);
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [checklistFromClockOut, setChecklistFromClockOut] = useState(false);
  const [finishingChecklist, setFinishingChecklist] = useState(false);

  // Week summary modal (can browse past weeks; admin can view any employee)
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryWeekStart, setSummaryWeekStart] = useState(getWeekStartStr());
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryUserId, setSummaryUserId] = useState('');

  // Entry editing
  const [editingEntry, setEditingEntry] = useState(null);
  const [editForm, setEditForm] = useState({ clockInTime: '', clockOutTime: '', reason: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);
  const [manualForm, setManualForm] = useState({ date: toDateInput(), clockInTime: '', clockOutTime: '', reason: '', notes: '' });
  const [savingManual, setSavingManual] = useState(false);

  // Personal expense (available any day, not just the Friday checklist)
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [expenseForm, setExpenseForm] = useState({ description: '', amount: '', notes: '' });
  const [savingExpense, setSavingExpense] = useState(false);

  const dayOfWeek = new Date().getDay();
  const isChecklistWindow = dayOfWeek === 5 || dayOfWeek === 6 || dayOfWeek === 0; // Fri-Sun
  const currentWeekStart = getWeekStartStr();
  const checklistCompleted = weekSummary?.checklist?.completed || false;

  // Load clock status
  const loadStatus = useCallback(async () => {
    try {
      const status = await api.getClockStatus();
      setClockStatus(status);
    } catch (err) {
      console.error('Failed to load clock status:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load current week summary (entries, pay, checklist)
  const loadWeek = useCallback(async () => {
    if (!isShopUser && !isAdmin) return;
    try {
      const summary = await api.getWeekSummary(currentWeekStart);
      setWeekSummary(summary);
      if (summary.checklist) {
        setChecklistItems(summary.checklist.items || {});
        const savedReturns = summary.checklist.returnsList || [];
        setReturnsList(savedReturns.length > 0 ? savedReturns : ['']);
        setNoReturns(summary.checklist.noReturns || false);
      }
    } catch (err) {
      console.error('Failed to load week summary:', err);
    }
  }, [isShopUser, isAdmin, currentWeekStart]);

  // Load time entries
  const loadEntries = useCallback(async () => {
    try {
      const params = {};
      if (filterUserId) params.userId = filterUserId;
      const entries = await api.getTimeEntries(params);
      setTimeEntries(entries);
    } catch (err) {
      console.error('Failed to load time entries:', err);
    }
  }, [filterUserId]);

  // Load users for admin filter
  const loadUsers = useCallback(async () => {
    if (isAdmin) {
      try {
        const usersData = await api.getUsers();
        setUsers(usersData.filter(u => u.role === 'shop' || u.role === 'admin'));
      } catch (err) {
        console.error('Failed to load users:', err);
      }
    }
  }, [isAdmin]);

  useEffect(() => {
    loadStatus();
    loadUsers();
    loadWeek();
  }, [loadStatus, loadUsers, loadWeek]);

  useEffect(() => {
    if (showHistory) {
      loadEntries();
    }
  }, [showHistory, loadEntries]);

  // Clock in
  const handleClockIn = async () => {
    setClockingIn(true);
    try {
      await api.clockIn();
      await loadStatus();
      await loadWeek();
    } catch (err) {
      alert('Failed to clock in: ' + err.message);
    } finally {
      setClockingIn(false);
    }
  };

  // Clock out
  const handleClockOut = async () => {
    if (!dailyNotes.trim()) {
      alert('Please enter what you did today before clocking out.');
      return;
    }
    
    setClockingOut(true);
    try {
      await api.clockOut(dailyNotes, false);
      setDailyNotes('');
      setShowClockOutModal(false);
      await loadStatus();
      await loadWeek();
      if (showHistory) await loadEntries();
      
      // Friday-Sunday: prompt the end-of-week checklist if not done yet
      if (isShopUser && isChecklistWindow && !checklistCompleted) {
        setChecklistFromClockOut(true);
        setShowChecklistModal(true);
      }
    } catch (err) {
      alert('Failed to clock out: ' + err.message);
    } finally {
      setClockingOut(false);
    }
  };

  // ============ PERSONAL EXPENSES ============

  const handleSaveExpense = async () => {
    const amt = parseFloat(expenseForm.amount);
    if (!expenseForm.description.trim()) {
      alert('Please enter what you bought.');
      return;
    }
    if (!amt || amt <= 0) {
      alert('Please enter a valid amount.');
      return;
    }
    setSavingExpense(true);
    try {
      await api.createShopExpense({
        expenseType: 'personal',
        description: expenseForm.description.trim(),
        amount: amt,
        notes: expenseForm.notes.trim() || undefined,
      });
      setShowExpenseModal(false);
      setExpenseForm({ description: '', amount: '', notes: '' });
      await loadWeek();
    } catch (err) {
      alert('Failed to add expense: ' + err.message);
    } finally {
      setSavingExpense(false);
    }
  };

  // ============ CHECKLIST ============

  const persistChecklist = async (items, returns, noRet) => {
    try {
      await api.saveWeeklyChecklist({
        weekStart: currentWeekStart,
        items,
        returnsList: returns.filter(r => r.trim()),
        noReturns: noRet,
      });
    } catch (err) {
      console.error('Failed to save checklist:', err);
    }
  };

  const toggleChecklistItem = (key) => {
    const updated = { ...checklistItems, [key]: !checklistItems[key] };
    setChecklistItems(updated);
    persistChecklist(updated, returnsList, noReturns);
  };

  const updateReturn = (idx, value) => {
    const updated = [...returnsList];
    updated[idx] = value;
    setReturnsList(updated);
  };

  const removeReturn = (idx) => {
    const updated = returnsList.filter((_, i) => i !== idx);
    const next = updated.length > 0 ? updated : [''];
    setReturnsList(next);
    persistChecklist(checklistItems, next, noReturns);
  };

  const returnsValid = noReturns || returnsList.some(r => r.trim());
  const allTasksChecked = CHECKLIST_TASKS.every(t => checklistItems[t.key]);
  const canFinishChecklist = allTasksChecked && returnsValid;
  const checkedCount = CHECKLIST_TASKS.filter(t => checklistItems[t.key]).length;

  const handleFinishChecklist = async () => {
    const wasAlreadyComplete = checklistCompleted;
    setFinishingChecklist(true);
    try {
      await api.saveWeeklyChecklist({
        weekStart: currentWeekStart,
        items: checklistItems,
        returnsList: returnsList.filter(r => r.trim()),
        noReturns,
        complete: true,
      });
      setShowChecklistModal(false);
      setChecklistFromClockOut(false);
      await loadWeek();
      // Show the summary on first completion; plain saves when editing later
      if (!wasAlreadyComplete) openSummary(currentWeekStart);
    } catch (err) {
      alert('Failed to save checklist: ' + err.message);
    } finally {
      setFinishingChecklist(false);
    }
  };

  const handleCloseChecklist = async () => {
    setShowChecklistModal(false);
    // Only counts as a "skip" (logged + notifies admin) when prompted at clock-out
    if (checklistFromClockOut && !checklistCompleted) {
      setChecklistFromClockOut(false);
      try {
        await api.skipWeeklyChecklist(currentWeekStart);
        await loadWeek();
      } catch (err) {
        console.error('Failed to log checklist skip:', err);
      }
    }
  };

  // ============ WEEK SUMMARY ============

  const openSummary = async (weekStart, userId = summaryUserId) => {
    setSummaryWeekStart(weekStart);
    setShowSummaryModal(true);
    setSummaryLoading(true);
    try {
      const data = await api.getWeekSummary(weekStart, isAdmin ? userId : undefined);
      setSummaryData(data);
    } catch (err) {
      alert('Failed to load week summary: ' + err.message);
    } finally {
      setSummaryLoading(false);
    }
  };

  const shiftSummaryWeek = (days) => {
    const d = new Date(summaryWeekStart + 'T00:00:00');
    d.setDate(d.getDate() + days);
    openSummary(getWeekStartStr(d));
  };

  const summaryEmployeeName = isAdmin
    ? (users.find(u => u.id === parseInt(summaryUserId))?.name || currentUser?.name)
    : currentUser?.name;

  const handlePrintSummary = () => {
    if (!summaryData) return;
    const s = summaryData;
    const rows = s.entries.map(e => `
      <tr>
        <td>${formatDate(e.clockIn)}${e.editedAt && !e.isManualEntry ? ' (edited)' : ''}${e.isManualEntry ? ' (manual)' : ''}</td>
        <td>${formatTime(e.clockIn)} - ${e.stillWorking ? 'working' : formatTime(e.clockOut)}</td>
        <td style="text-align:right">${e.hours.toFixed(2)} hrs</td>
      </tr>`).join('');
    const returns = (s.checklist?.returnsList || []).map(r => `<li>${r}</li>`).join('');
    const win = window.open('', '_blank');
    win.document.write(`
      <html><head><title>Week Summary — ${s.weekStart}</title>
      <style>body{font-family:sans-serif;padding:24px;max-width:600px;margin:0 auto}table{width:100%;border-collapse:collapse}td,th{padding:6px 4px;border-bottom:1px solid #ddd;text-align:left}.total{font-weight:bold;font-size:18px}</style>
      </head><body>
      <h2>Week Summary — ${summaryEmployeeName || ''}</h2>
      <p>Week of ${s.weekStart} to ${s.weekEnd}</p>
      <table>${rows}</table>
      <table style="margin-top:16px">
        <tr><td>Total hours</td><td style="text-align:right">${s.totalHours.toFixed(2)} hrs</td></tr>
        <tr><td>Hourly wage</td><td style="text-align:right">${fmtMoney(s.hourlyWage)}</td></tr>
        <tr><td>Gross pay</td><td style="text-align:right">${fmtMoney(s.grossPay)}</td></tr>
        <tr><td>Personal purchases (${s.personalExpenses.length})</td><td style="text-align:right">-${fmtMoney(s.personalTotal)}</td></tr>
        <tr class="total"><td>Owed</td><td style="text-align:right">${fmtMoney(s.netPay)}</td></tr>
      </table>
      ${returns ? `<h3>Returns sent to office</h3><ul>${returns}</ul>` : ''}
      </body></html>
    `);
    win.document.close();
    win.print();
  };

  // ============ ENTRY EDITING ============

  const openEditModal = (entry) => {
    setEditingEntry(entry);
    setEditForm({
      clockInTime: toTimeInput(entry.clockIn),
      clockOutTime: toTimeInput(entry.clockOut),
      reason: '',
    });
  };

  const handleSaveEdit = async () => {
    if (!editForm.reason.trim()) {
      alert('Please enter a reason for this change.');
      return;
    }
    if (!editForm.clockInTime) {
      alert('Clock in time is required.');
      return;
    }
    const dateStr = toDateInput(editingEntry.clockIn);
    const clockIn = new Date(`${dateStr}T${editForm.clockInTime}`);
    let clockOut = null;
    if (editForm.clockOutTime) {
      clockOut = new Date(`${dateStr}T${editForm.clockOutTime}`);
      if (clockOut <= clockIn) {
        alert('Clock out must be after clock in.');
        return;
      }
    } else if (editingEntry.clockOut) {
      alert('Clock out time is required.');
      return;
    }
    
    setSavingEdit(true);
    try {
      await api.updateTimeEntry(editingEntry.id, {
        clockIn: clockIn.toISOString(),
        clockOut: clockOut ? clockOut.toISOString() : null,
        editReason: editForm.reason.trim(),
        tookLunch: false,
      });
      setEditingEntry(null);
      await loadWeek();
      await loadStatus();
      if (showHistory) await loadEntries();
    } catch (err) {
      alert('Failed to save change: ' + err.message);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleSaveManual = async () => {
    if (!manualForm.reason.trim()) {
      alert('Please enter a reason for adding this entry.');
      return;
    }
    if (!manualForm.date || !manualForm.clockInTime || !manualForm.clockOutTime) {
      alert('Date, clock in, and clock out times are required.');
      return;
    }
    const clockIn = new Date(`${manualForm.date}T${manualForm.clockInTime}`);
    const clockOut = new Date(`${manualForm.date}T${manualForm.clockOutTime}`);
    if (clockOut <= clockIn) {
      alert('Clock out must be after clock in.');
      return;
    }
    
    setSavingManual(true);
    try {
      await api.createManualTimeEntry({
        clockIn: clockIn.toISOString(),
        clockOut: clockOut.toISOString(),
        editReason: manualForm.reason.trim(),
        tookLunch: false,
        dailyNotes: manualForm.notes,
      });
      setShowManualModal(false);
      setManualForm({ date: toDateInput(), clockInTime: '', clockOutTime: '', reason: '', notes: '' });
      await loadWeek();
      if (showHistory) await loadEntries();
    } catch (err) {
      alert('Failed to add entry: ' + err.message);
    } finally {
      setSavingManual(false);
    }
  };

  // ============ FORMATTERS ============

  const formatDuration = (start, end) => {
    const startTime = new Date(start);
    const endTime = end ? new Date(end) : new Date();
    const diffMs = endTime - startTime;
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric'
    });
  };

  const renderEditBadges = (entry) => (
    <>
      {entry.isManualEntry && (
        <span className="ml-2 text-xs font-semibold text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">Added Manually</span>
      )}
      {entry.editedAt && !entry.isManualEntry && (
        <span className="ml-2 text-xs font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Edited</span>
      )}
    </>
  );

  if (loading) {
    return (
      <div className="bg-white border border-neutral-200 rounded-xl p-4">
        <div className="text-center text-neutral-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* End-of-Week Checklist Banner (Fri-Sun until complete) */}
      {isShopUser && isChecklistWindow && !checklistCompleted && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="font-bold text-sm">End-of-Week Checklist</div>
            <div className="text-sm text-amber-800">{checkedCount} of {CHECKLIST_TASKS.length} tasks done — finish before you head out</div>
          </div>
          <button
            onClick={() => { setChecklistFromClockOut(false); setShowChecklistModal(true); }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
          >
            Open Checklist
          </button>
        </div>
      )}

      {/* Checklist done banner */}
      {isShopUser && isChecklistWindow && checklistCompleted && (
        <div className="bg-green-50 border border-green-300 rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm font-medium text-green-800">End-of-week checklist complete</div>
          <div className="flex gap-3">
            <button
              onClick={() => { setChecklistFromClockOut(false); setShowChecklistModal(true); }}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Edit Checklist
            </button>
            <button
              onClick={() => openSummary(currentWeekStart)}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              View Week Summary
            </button>
          </div>
        </div>
      )}

      {/* Clock Status Card */}
      <div className={`border-2 rounded-xl p-6 ${
        clockStatus.clockedIn 
          ? 'bg-green-50 border-green-300' 
          : 'bg-white border-neutral-200'
      }`}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
               Time Clock
              {clockStatus.clockedIn && (
                <span className="text-sm font-normal text-green-600 bg-green-100 px-2 py-0.5 rounded">
                  Clocked In
                </span>
              )}
            </h2>
            {clockStatus.clockedIn && clockStatus.entry && (
              <div className="mt-2 text-sm text-neutral-600">
                <div>Started: {formatTime(clockStatus.entry.clockIn)}</div>
                <div>Duration: {formatDuration(clockStatus.entry.clockIn)}</div>
              </div>
            )}
          </div>
          
          <div>
            {clockStatus.clockedIn ? (
              <button
                onClick={() => setShowClockOutModal(true)}
                className="px-6 py-3 bg-red-600 text-white rounded-lg font-bold text-lg hover:bg-red-700 transition"
              >
                 Clock Out
              </button>
            ) : (
              <button
                onClick={handleClockIn}
                disabled={clockingIn}
                className="px-6 py-3 bg-green-600 text-white rounded-lg font-bold text-lg hover:bg-green-700 transition disabled:opacity-50"
              >
                {clockingIn ? 'Clocking In...' : ' Clock In'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* My Week - editable timesheet for the current week */}
      {isShopUser && weekSummary && (
        <div className="bg-white border border-neutral-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <h3 className="font-semibold">My Week</h3>
              <div className="text-xs text-neutral-500">
                {formatDate(weekSummary.weekStart + 'T00:00:00')} – {formatDate(weekSummary.weekEnd + 'T00:00:00')}
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowExpenseModal(true)}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
              >
                + Personal Expense
              </button>
              <button
                onClick={() => openSummary(currentWeekStart)}
                className="px-3 py-1.5 text-sm border border-neutral-200 rounded-lg hover:bg-neutral-50"
              >
                Week Summary
              </button>
              <button
                onClick={() => setShowManualModal(true)}
                className="px-3 py-1.5 text-sm border border-neutral-200 rounded-lg hover:bg-neutral-50"
              >
                + Add Missed Day
              </button>
            </div>
          </div>

          {weekSummary.entries.length === 0 ? (
            <div className="text-center text-neutral-400 py-4 text-sm">No time entries this week yet</div>
          ) : (
            <div className="space-y-2">
              {weekSummary.entries.map(entry => (
                <div
                  key={entry.id}
                  className={`border rounded-lg p-3 ${
                    entry.stillWorking ? 'bg-green-50 border-green-200'
                    : (entry.editedAt || entry.isManualEntry) ? 'bg-amber-50/50 border-amber-200'
                    : 'border-neutral-200'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-medium text-sm">
                        {formatDate(entry.clockIn)}
                        {renderEditBadges(entry)}
                      </div>
                      <div className="text-sm text-neutral-600">
                        {entry.originalClockIn && new Date(entry.originalClockIn).getTime() !== new Date(entry.clockIn).getTime() && (
                          <span className="line-through text-neutral-400 mr-1">{formatTime(entry.originalClockIn)}</span>
                        )}
                        {formatTime(entry.clockIn)} – {entry.stillWorking ? (
                          <span className="text-green-600">Still working</span>
                        ) : (
                          <>
                            {entry.originalClockOut && new Date(entry.originalClockOut).getTime() !== new Date(entry.clockOut).getTime() && (
                              <span className="line-through text-neutral-400 mr-1">{formatTime(entry.originalClockOut)}</span>
                            )}
                            {formatTime(entry.clockOut)}
                          </>
                        )}
                      </div>
                      {entry.editReason && (
                        <div className="text-xs text-amber-700 mt-1">Reason: "{entry.editReason}"</div>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="font-mono font-bold text-sm">{entry.hours.toFixed(2)} hrs</div>
                      {!entry.stillWorking && (
                        <button
                          onClick={() => openEditModal(entry)}
                          className="text-xs text-blue-600 hover:text-blue-800 mt-1"
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {weekSummary.personalExpenses?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-neutral-100">
              <div className="text-sm font-semibold mb-1">Personal purchases this week</div>
              {weekSummary.personalExpenses.map(e => (
                <div key={e.id} className="flex justify-between text-sm text-neutral-600 py-0.5">
                  <span>{e.description}</span>
                  <span className="font-mono">−{fmtMoney(e.amount)}</span>
                </div>
              ))}
              <div className="flex justify-between text-sm font-semibold text-red-600 mt-1 pt-1 border-t border-neutral-100">
                <span>Deducted from this week's pay</span>
                <span className="font-mono">−{fmtMoney(weekSummary.personalTotal)}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Clock Out Modal */}
      {showClockOutModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
            <h3 className="text-xl font-bold mb-4"> Clock Out</h3>
            
            <div className="mb-4">
              <label className="block font-medium mb-2">
                What did you do today? <span className="text-red-500">*</span>
              </label>
              <textarea
                value={dailyNotes}
                onChange={e => setDailyNotes(e.target.value)}
                className="w-full h-32 px-4 py-3 border border-neutral-200 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Describe the work you completed today...

• Which vehicles did you work on?
• What tasks did you complete?
• Any issues or notes for tomorrow?"
                autoFocus
              />
              <div className="text-xs text-neutral-500 mt-1">
                This is required to clock out.
              </div>
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowClockOutModal(false);
                  setDailyNotes('');
                }}
                className="px-4 py-2 text-neutral-600 hover:bg-neutral-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleClockOut}
                disabled={clockingOut || !dailyNotes.trim()}
                className="px-6 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50"
              >
                {clockingOut ? 'Clocking Out...' : 'Clock Out'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Personal Expense Modal */}
      {showExpenseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-1">Add Personal Expense</h3>
            <div className="text-sm text-neutral-500 mb-4">
              Anything you bought on the card for yourself. It comes out of this week's pay.
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">
                What did you buy? <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={expenseForm.description}
                onChange={e => setExpenseForm({ ...expenseForm, description: e.target.value })}
                placeholder="e.g. Drinks at the gas station"
                className="w-full px-3 py-2 border border-neutral-200 rounded-lg"
                autoFocus
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">
                Amount <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                value={expenseForm.amount}
                onChange={e => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                placeholder="0.00"
                className="w-full px-3 py-2 border border-neutral-200 rounded-lg"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Notes (optional)</label>
              <input
                type="text"
                value={expenseForm.notes}
                onChange={e => setExpenseForm({ ...expenseForm, notes: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-200 rounded-lg"
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowExpenseModal(false)}
                className="px-4 py-2 text-neutral-600 hover:bg-neutral-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveExpense}
                disabled={savingExpense || !expenseForm.description.trim() || !parseFloat(expenseForm.amount)}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {savingExpense ? 'Adding...' : 'Add Expense'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* End-of-Week Checklist Modal */}
      {showChecklistModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 my-8">
            <h3 className="text-xl font-bold mb-1">End-of-Week Checklist</h3>
            <div className="text-sm text-neutral-500 mb-4">
              Week of {formatDate(currentWeekStart + 'T00:00:00')}
            </div>

            <div className="space-y-2">
              {CHECKLIST_TASKS.map(task => (
                <div
                  key={task.key}
                  className={`border rounded-lg p-3 ${checklistItems[task.key] ? 'bg-green-50 border-green-200' : 'border-neutral-200'}`}
                >
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!checklistItems[task.key]}
                      onChange={() => toggleChecklistItem(task.key)}
                      className="w-5 h-5 rounded border-neutral-300 mt-0.5 flex-shrink-0"
                    />
                    <div className="flex-1">
                      <div className="font-semibold text-sm">{task.title}</div>
                      <div className="text-xs text-neutral-500">{task.desc}</div>
                    </div>
                  </label>

                  {task.key === 'returns' && (
                    <div className="mt-3 ml-8 space-y-2">
                      {!noReturns && returnsList.map((ret, idx) => (
                        <div key={idx} className="flex gap-2">
                          <input
                            type="text"
                            value={ret}
                            onChange={e => updateReturn(idx, e.target.value)}
                            onBlur={() => persistChecklist(checklistItems, returnsList, noReturns)}
                            placeholder="Part — vendor — reason"
                            className="flex-1 px-3 py-1.5 border border-neutral-200 rounded-lg text-sm"
                          />
                          <button
                            onClick={() => removeReturn(idx)}
                            className="px-2 text-red-500 hover:bg-red-50 rounded"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                      {!noReturns && (
                        <button
                          onClick={() => setReturnsList([...returnsList, ''])}
                          className="text-xs px-3 py-1.5 border border-neutral-200 rounded-lg hover:bg-neutral-50"
                        >
                          + Add another return
                        </button>
                      )}
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={noReturns}
                          onChange={e => {
                            setNoReturns(e.target.checked);
                            persistChecklist(checklistItems, returnsList, e.target.checked);
                          }}
                          className="w-4 h-4 rounded border-neutral-300"
                        />
                        No returns this week
                      </label>
                    </div>
                  )}

                  {task.key === 'purchases' && (
                    <div className="mt-2 ml-8">
                      <button
                        onClick={() => { window.location.hash = 'expenses'; }}
                        className="text-xs px-3 py-1.5 border border-neutral-200 rounded-lg hover:bg-neutral-50"
                      >
                        Open Shop Expenses →
                      </button>
                    </div>
                  )}

                  {task.key === 'timesheet' && (
                    <div className="mt-2 ml-8">
                      <button
                        onClick={() => setShowChecklistModal(false)}
                        className="text-xs px-3 py-1.5 border border-neutral-200 rounded-lg hover:bg-neutral-50"
                      >
                        Review My Week →
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between mt-5 gap-3 flex-wrap">
              <button
                onClick={handleCloseChecklist}
                className="text-sm text-neutral-500 hover:text-neutral-700"
              >
                {checklistFromClockOut ? 'Skip for now' : 'Close'}
              </button>
              <button
                onClick={handleFinishChecklist}
                disabled={!canFinishChecklist || finishingChecklist}
                className="px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50"
              >
                {finishingChecklist ? 'Saving...' : (checklistCompleted ? 'Save Changes' : 'Finish Checklist')}
              </button>
            </div>
            {!canFinishChecklist && (
              <div className="text-xs text-neutral-400 mt-2 text-right">
                Check all tasks{!returnsValid ? ' and list returns (or check "No returns")' : ''} to finish
              </div>
            )}
          </div>
        </div>
      )}

      {/* Week Summary Modal */}
      {showSummaryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 my-8">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-xl font-bold">Week Summary</h3>
              <div className="flex gap-2">
                <button
                  onClick={handlePrintSummary}
                  disabled={!summaryData}
                  className="px-3 py-1.5 text-sm border border-neutral-200 rounded-lg hover:bg-neutral-50 disabled:opacity-50"
                >
                  Print
                </button>
                <button
                  onClick={() => setShowSummaryModal(false)}
                  className="px-3 py-1.5 text-sm text-neutral-500 hover:bg-neutral-100 rounded-lg"
                >
                  Close
                </button>
              </div>
            </div>

            {isAdmin && (
              <div className="mb-3">
                <select
                  value={summaryUserId}
                  onChange={e => {
                    setSummaryUserId(e.target.value);
                    openSummary(summaryWeekStart, e.target.value);
                  }}
                  className="w-full h-9 px-3 border border-neutral-200 rounded-lg text-sm"
                >
                  <option value="">Me ({currentUser?.name})</option>
                  {users.filter(u => u.id !== currentUser?.id).map(u => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex items-center justify-between mb-4">
              <button onClick={() => shiftSummaryWeek(-7)} className="text-sm text-blue-600 hover:text-blue-800">← Prev week</button>
              <div className="text-sm text-neutral-500">
                {formatDate(summaryWeekStart + 'T00:00:00')}
                {summaryData && <> – {formatDate(summaryData.weekEnd + 'T00:00:00')}</>}
              </div>
              <button
                onClick={() => shiftSummaryWeek(7)}
                disabled={summaryWeekStart >= currentWeekStart}
                className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-30"
              >
                Next week →
              </button>
            </div>

            {summaryLoading ? (
              <div className="text-center text-neutral-400 py-8">Loading...</div>
            ) : summaryData ? (
              <>
                {summaryData.checklist?.completed && (
                  <div className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-3">
                    Checklist finished {new Date(summaryData.checklist.completedAt).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  </div>
                )}

                <div className="divide-y divide-neutral-100">
                  {summaryData.entries.length === 0 ? (
                    <div className="text-center text-neutral-400 py-4 text-sm">No time entries this week</div>
                  ) : summaryData.entries.map(entry => (
                    <div key={entry.id} className="flex justify-between py-2 text-sm">
                      <span>
                        {formatDate(entry.clockIn)}{' '}
                        <span className="text-neutral-400">{formatTime(entry.clockIn)} – {entry.stillWorking ? 'working' : formatTime(entry.clockOut)}</span>
                        {renderEditBadges(entry)}
                      </span>
                      <span className="font-mono">{entry.hours.toFixed(2)} hrs</span>
                    </div>
                  ))}
                </div>

                <div className="mt-3 pt-3 border-t border-neutral-200 space-y-1.5 text-sm">
                  <div className="flex justify-between font-semibold">
                    <span>Total hours</span>
                    <span className="font-mono">{summaryData.totalHours.toFixed(2)} hrs</span>
                  </div>
                  <div className="flex justify-between text-neutral-600">
                    <span>Hourly wage</span>
                    <span className="font-mono">{fmtMoney(summaryData.hourlyWage)}</span>
                  </div>
                  <div className="flex justify-between text-neutral-600">
                    <span>Gross pay</span>
                    <span className="font-mono">{fmtMoney(summaryData.grossPay)}</span>
                  </div>
                  <div className="flex justify-between text-red-600">
                    <span>Personal purchases ({summaryData.personalExpenses.length})</span>
                    <span className="font-mono">−{fmtMoney(summaryData.personalTotal)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg pt-2 border-t-2 border-neutral-800">
                    <span>Owed</span>
                    <span className={`font-mono ${summaryData.netPay >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {summaryData.netPay < 0 ? '−' : ''}{fmtMoney(summaryData.netPay)}
                    </span>
                  </div>
                </div>

                {summaryData.checklist?.returnsList?.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-neutral-100">
                    <div className="text-sm font-semibold mb-1">Returns sent to office ({summaryData.checklist.returnsList.length}):</div>
                    {summaryData.checklist.returnsList.map((r, i) => (
                      <div key={i} className="text-sm text-neutral-500">• {r}</div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="text-center text-neutral-400 py-8">No data</div>
            )}
          </div>
        </div>
      )}

      {/* Edit Entry Modal */}
      {editingEntry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Edit Time Entry — {formatDate(editingEntry.clockIn)}</h3>
            
            <div className="flex gap-3 mb-4">
              <div className="flex-1">
                <label className="block text-sm font-medium mb-1">Clock In</label>
                <input
                  type="time"
                  value={editForm.clockInTime}
                  onChange={e => setEditForm({ ...editForm, clockInTime: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-200 rounded-lg"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium mb-1">Clock Out</label>
                <input
                  type="time"
                  value={editForm.clockOutTime}
                  onChange={e => setEditForm({ ...editForm, clockOutTime: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-200 rounded-lg"
                />
              </div>
            </div>
            
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">
                Why are you changing this? <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={editForm.reason}
                onChange={e => setEditForm({ ...editForm, reason: e.target.value })}
                placeholder="e.g. Forgot to clock in when I got here"
                className="w-full px-3 py-2 border border-neutral-200 rounded-lg"
                autoFocus
              />
            </div>

            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
              The original time stays on record.
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setEditingEntry(null)}
                className="px-4 py-2 text-neutral-600 hover:bg-neutral-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={savingEdit || !editForm.reason.trim()}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {savingEdit ? 'Saving...' : 'Save Change'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Missed Day Modal */}
      {showManualModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">+ Add Missed Day</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Date (this week only)</label>
              <input
                type="date"
                value={manualForm.date}
                onChange={e => setManualForm({ ...manualForm, date: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-200 rounded-lg"
              />
            </div>

            <div className="flex gap-3 mb-4">
              <div className="flex-1">
                <label className="block text-sm font-medium mb-1">Clock In</label>
                <input
                  type="time"
                  value={manualForm.clockInTime}
                  onChange={e => setManualForm({ ...manualForm, clockInTime: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-200 rounded-lg"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium mb-1">Clock Out</label>
                <input
                  type="time"
                  value={manualForm.clockOutTime}
                  onChange={e => setManualForm({ ...manualForm, clockOutTime: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-200 rounded-lg"
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">
                Why was this day missed? <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={manualForm.reason}
                onChange={e => setManualForm({ ...manualForm, reason: e.target.value })}
                placeholder="e.g. Forgot to clock in — was at the auction"
                className="w-full px-3 py-2 border border-neutral-200 rounded-lg"
              />
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">What did you do that day? (optional)</label>
              <textarea
                value={manualForm.notes}
                onChange={e => setManualForm({ ...manualForm, notes: e.target.value })}
                className="w-full h-20 px-3 py-2 border border-neutral-200 rounded-lg resize-none"
              />
            </div>

            <div className="text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 mb-4">
              This entry will be marked as manually added.
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowManualModal(false)}
                className="px-4 py-2 text-neutral-600 hover:bg-neutral-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveManual}
                disabled={savingManual || !manualForm.reason.trim()}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {savingManual ? 'Adding...' : 'Add Entry'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Toggle */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="text-sm text-blue-600 hover:text-blue-800"
        >
          {showHistory ? '▼ Hide History' : '▶ Show Time History'}
        </button>
        {isAdmin && (
          <button
            onClick={() => openSummary(currentWeekStart)}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            Week Summaries
          </button>
        )}
      </div>

      {/* Time History */}
      {showHistory && (
        <div className="bg-white border border-neutral-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Time History</h3>
            
            {isAdmin && (
              <select
                value={filterUserId}
                onChange={e => setFilterUserId(e.target.value)}
                className="h-8 px-3 border border-neutral-200 rounded text-sm"
              >
                <option value="">All Employees</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            )}
          </div>
          
          {timeEntries.length === 0 ? (
            <div className="text-center text-neutral-400 py-8">
              No time entries found
            </div>
          ) : (
            <div className="space-y-3">
              {timeEntries.map(entry => (
                <div 
                  key={entry.id} 
                  className={`border rounded-lg p-3 ${
                    !entry.clockOut ? 'bg-green-50 border-green-200' : 'border-neutral-200'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-medium">
                        {formatDate(entry.clockIn)}
                        {renderEditBadges(entry)}
                        {isAdmin && entry.userName && (
                          <span className="ml-2 text-sm text-neutral-500">
                            ({entry.userName})
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-neutral-600">
                        {formatTime(entry.clockIn)} - {entry.clockOut ? formatTime(entry.clockOut) : 'Still working'}
                      </div>
                      {(entry.editedAt || entry.isManualEntry) && (
                        <div className="text-xs text-amber-700 mt-1">
                          {entry.originalClockIn && (
                            <span>Original: {formatTime(entry.originalClockIn)} - {formatTime(entry.originalClockOut)} · </span>
                          )}
                          {entry.editReason && <span>Reason: "{entry.editReason}"</span>}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="font-mono font-bold">
                        {formatDuration(entry.clockIn, entry.clockOut)}
                      </div>
                      {!entry.clockOut && (
                        <span className="text-xs text-green-600">Active</span>
                      )}
                    </div>
                  </div>
                  
                  {entry.dailyNotes && (
                    <div className="mt-2 pt-2 border-t border-neutral-100">
                      <div className="text-xs text-neutral-500 mb-1">Notes:</div>
                      <div className="text-sm whitespace-pre-wrap">{entry.dailyNotes}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TimeClock;
